import React, { useRef, useState } from 'react'
import * as d3 from 'd3'
import useSize from 'hooks/useSize'
import styled from 'styled-components'

const isMobile = window.innerWidth < 900

interface FortunesData {
  year: number
  fortune: number
}

interface FortuneChartProps {
  rawData: FortunesData[]
}

interface TooltipProps {
  position: [number, number]
  text: string
}

export const fillMissingValues = (data: any): FortunesData[] => {
  let lastValue: number | null = null

  return data.map((item: FortunesData) => {
    if (item.fortune !== null) {
      lastValue = item.fortune
      return item
    }
    return { ...item, fortune: lastValue }
  })
}

export const FortuneChart = ({ rawData }: FortuneChartProps) => {
  const data = fillMissingValues(rawData)
  const [tooltipData, setTooltipData] = useState<TooltipProps | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [container, size] = useSize()
  const width = size.width
  const height = width * (isMobile ? 0.5 : 0.35)
  const gutterTop = 10
  const gutterRight = 20
  const gutterLeft = 70
  const gutterBottom = 0
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom,
  }

  const [minFortune, maxFortune] = [
    (d3.min(data, (d) => d.fortune) || 0) * 0.75,
    (d3.max(data, (d) => d.fortune) || 0) * 1.25,
  ]

  const [minYear, maxYear] = [
    d3.min(data, (d) => d.year) || 0,
    d3.max(data, (d) => d.year) || 0,
  ]

  const xAxisScale = React.useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([minYear, maxYear])
        .range([plotArea.left, plotArea.right]),
    [size, data]
  )

  const yAxisScale = React.useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([minFortune * 0.75, maxFortune * 1.25])
        .range([plotArea.bottom, plotArea.top]),
    [size, data]
  )

  const xFunction = (d: FortunesData) => xAxisScale(d.year) || 0
  const yFunction = (d: FortunesData) => yAxisScale(d.fortune) || 0

  const lineFunction = React.useMemo(
    () =>
      d3
        .line<FortunesData>()
        .x((d) => xFunction(d))
        .y((d) => yFunction(d)),
    [size, data]
  )

  const xTicks = React.useMemo(
    () =>
      xAxisScale
        .ticks(isMobile ? 3 : 5)
        .filter((tick) => Number.isInteger(tick)),
    [size, data]
  )

  return (
    <Container ref={container}>
      <>
        <div className='y-axis'>
          {[
            minFortune,
            minFortune + (maxFortune - minFortune) / 2,
            maxFortune,
          ].map((f, i) => (
            <div
              key={`y-axis-tick-line-${i}`}
              style={{ transform: `translateY(${yAxisScale(f)}px)` }}
              className='y-axis-tick'
            >
              <GridLineY
                style={{
                  width: width - plotArea.left,
                  transform: `translateX(${plotArea.left - 10}px)`,
                }}
              />
            </div>
          ))}
        </div>
        {/* <div
          className='legend'
          style={{ position: 'absolute', bottom: 16, left: 0 }}
        >
          {data.map((d, i) => (
            <div
              style={{ transform: `translateX(${xAxisScale(d.year)}px)` }}
              className='tick'
              key={`chart-tick-${i}`}
            >
              <GridLineX
                style={{
                  height: 10,
                  transform: `translateY(${0}px)`,
                }}
              />
            </div>
          ))}
        </div> */}
      </>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ position: 'relative' }}
      >
        <Path d={lineFunction(data) || undefined} />
        {data.map((d, i) => (
          <Circle
            onMouseEnter={() => {
              setTooltipData({
                position: [xAxisScale(d.year), yAxisScale(d.fortune)],
                text: d.fortune.toString(),
              })
            }}
            onMouseLeave={() => {
              setTooltipData(null)
            }}
            key={`chart-circle-${i}`}
            cx={xAxisScale(d.year)}
            cy={yAxisScale(d.fortune)}
          />
        ))}
      </svg>
      <>
        {/* {tooltipData && (
          <Tooltip
            style={{
              left: tooltipData.position[0],
              top: tooltipData.position[1],
            }}
          >
            {tooltipData.text}
          </Tooltip>
        )} */}
        <Years>
          {xTicks.map((d, i) => (
            <Tick
              style={{
                left: xAxisScale(d),
                transform: `translateX(-50%)`,
                textAlign: 'center',
              }}
              key={`chart-tick-${i}`}
            >
              <div>{d}</div>
            </Tick>
          ))}
        </Years>
        <YAxis>
          {[
            minFortune,
            minFortune + (maxFortune - minFortune) / 2,
            maxFortune,
          ].map((f, i) => (
            <Tick
              key={`y-axis-tick-label-${i}`}
              style={{ top: yAxisScale(f), transform: `translateY(-50%)` }}
            >
              {f >= 1000 ? (f / 1000).toFixed(1) : f}{' '}
              {i === 2 ? (f >= 1000 ? 'Md' : 'Mo') : ''}
            </Tick>
          ))}
        </YAxis>
      </>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  font-family: var(--font-sans-serif);
  font-weight: 400;
  color: ${({ theme }) => theme.textColor};
`

const Path = styled.path`
  fill: none;
  stroke: ${({ theme }) => theme.colorMain};
  stroke-width: 3px;
  stroke-linejoin: round;
  stroke-linecap: round;
`

const YAxis = styled.div`
  font-size: 0.75em;
  position: absolute;
  top: 0;
  left: 0;
`

const Years = styled.div`
  font-size: 0.75em;
  position: absolute;
  bottom: 10px;
  left: 0;
`

const Tick = styled.div`
  position: absolute;
  line-height: 1;
  top: 0;
  left: 0;
  width: 50px;
  text-align: right;
`

const GridLineY = styled.div`
  border-top: 1px solid hsla(0, 0%, 67.1%, 0.4);
  top: 0;
  left: 0;
  position: absolute;
`

const GridLineX = styled.div`
  border-left: 1px solid hsla(0, 0%, 67.1%, 0.4);
  top: 0;
  left: 0;
  position: absolute;
`

const Circle = styled.circle`
  fill: ${({ theme }) => theme.colorMain};
  r: 3px;
`

const Tooltip = styled.div`
  position: absolute;
  background-color: var(--site-background);
  border-radius: 5px;
  padding: 5px 5px;
  font-size: 0.5em;
  line-height: 1;
`
